import { PaymentSummarySheet } from "@/components/booking/PaymentSummarySheet";
import { getStripe } from "@/components/stripe/stripe-web";
import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { invalidateBookingQueries } from "@/hooks/queries/use-bookings-query";
import { showAlert } from "@/lib/alert";
import { isCustomerRole } from "@/lib/roles";
import bookingService, { Booking } from "@/lib/services/bookings";
import paymentService from "@/lib/services/payment";
import { showToast } from "@/lib/toast";
import { User } from "@/lib/types/user";
import Ionicons from "@react-native-vector-icons/ionicons";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";

interface BookingPaymentProps {
  booking: Booking;
  user: User | null;
  onSuccess: (updatedBooking: Booking) => void;
}

interface PaymentModalProps {
  booking: Booking;
  clientSecret: string;
  paymentIntentId: string;
  amount: number;
  walletAmount: number;
  onClose: () => void;
  onSuccess: (updatedBooking: Booking) => void;
}

const handledPaymentIntentIds = new Set<string>();

const STRIPE_RETURN_PARAM_KEYS = [
  "payment_intent",
  "payment_intent_client_secret",
  "redirect_status",
  "redirect_pm_type",
] as const;

function paymentReturnUrl(): string {
  const url = new URL(window.location.href);

  for (const key of STRIPE_RETURN_PARAM_KEYS) {
    url.searchParams.delete(key);
  }

  return url.toString();
}

function clearPaymentReturnParams(): void {
  const url = new URL(window.location.href);

  for (const key of STRIPE_RETURN_PARAM_KEYS) {
    url.searchParams.delete(key);
  }

  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
}

function isPayableIntentStatus(status: string | undefined): boolean {
  return status === "requires_capture" || status === "succeeded";
}

async function recordAuthorizedPayment(
  qc: QueryClient,
  booking: Booking,
  paymentIntentId: string,
  onSuccess: (updatedBooking: Booking) => void,
): Promise<void> {
  await bookingService.authorizePayment(booking.id, paymentIntentId);

  invalidateBookingQueries(qc, booking.id);

  const updatedBooking = await bookingService.getBooking(booking.id);
  onSuccess(updatedBooking);

  showToast.success(
    "Your payment has been authorized successfully. The driver has been notified.",
  );
}

function WebPaymentForm({
  booking,
  paymentIntentId,
  amount,
  walletAmount,
  onClose,
  onSuccess,
}: Omit<PaymentModalProps, "clientSecret">) {
  const qc = useQueryClient();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }

    setSubmitting(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: paymentReturnUrl(),
        },
        redirect: "if_required",
      });

      if (error) {
        showToast.error(error.message ?? "Payment failed");
        return;
      }

      if (!isPayableIntentStatus(paymentIntent?.status)) {
        showToast.error("Payment was not completed. Please try again.");
        return;
      }

      await recordAuthorizedPayment(qc, booking, paymentIntentId, onSuccess);
      onClose();
    } catch (error: any) {
      console.error("Payment error:", error);
      showToast.error(error.response?.data?.message || "Payment failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View style={styles.modalContent}>
      <View style={styles.modalHeader}>
        <ThemedText type="defaultSemiBold" style={styles.modalTitle}>
          Pay £{Number(amount).toFixed(2)}
        </ThemedText>
        <TouchableOpacity
          onPress={onClose}
          disabled={submitting}
          accessibilityLabel="Close payment"
        >
          <Ionicons name="close" size={24} color={BrandColors.charcoal} />
        </TouchableOpacity>
      </View>

      {walletAmount > 0 && (
        <ThemedText style={styles.modalSubtitle}>
          £{Number(walletAmount).toFixed(2)} wallet credit applied
        </ThemedText>
      )}

      <ThemedText style={styles.modalSubtitle}>
        Your card will be authorized now and charged when the job is completed.
      </ThemedText>

      <View style={styles.paymentElementContainer}>
        <PaymentElement />
      </View>

      <TouchableOpacity
        style={[
          styles.actionButton,
          styles.payButton,
          submitting && styles.disabledButton,
        ]}
        onPress={handleSubmit}
        disabled={submitting || !stripe || !elements}
      >
        {submitting ? (
          <ActivityIndicator color="#fff" />
        ) : (
          <>
            <Ionicons name="card" size={20} color="#fff" />
            <ThemedText style={styles.actionButtonText} type="defaultSemiBold">
              Authorize Payment
            </ThemedText>
          </>
        )}
      </TouchableOpacity>
    </View>
  );
}

function WebPaymentModal({
  booking,
  clientSecret,
  paymentIntentId,
  amount,
  walletAmount,
  onClose,
  onSuccess,
}: PaymentModalProps) {
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalCard}>
          <Elements
            stripe={getStripe()}
            options={{
              clientSecret,
              appearance: {
                theme: "stripe",
                variables: {
                  colorPrimary: BrandColors.success,
                  borderRadius: "12px",
                },
              },
            }}
          >
            <WebPaymentForm
              booking={booking}
              paymentIntentId={paymentIntentId}
              amount={amount}
              walletAmount={walletAmount}
              onClose={onClose}
              onSuccess={onSuccess}
            />
          </Elements>
        </View>
      </View>
    </Modal>
  );
}

export const BookingPayment = ({
  booking,
  user,
  onSuccess,
}: BookingPaymentProps) => {
  const qc = useQueryClient();
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState<"pay" | "cash" | null>(null);
  const [paymentSession, setPaymentSession] = useState<{
    clientSecret: string;
    paymentIntentId: string;
    amount: number;
    walletAmount: number;
  } | null>(null);
  const [summaryVisible, setSummaryVisible] = useState(false);

  const handlePayNow = async (useWalletBalance: boolean) => {
    if (!booking) {
      return;
    }

    setLoading("pay");

    try {
      const {
        client_secret,
        payment_intent_id,
        amount,
        wallet_amount_gbp,
      } = await bookingService.preparePayment(booking.id, useWalletBalance);

      setSummaryVisible(false);
      setPaymentSession({
        clientSecret: client_secret,
        paymentIntentId: payment_intent_id,
        amount,
        walletAmount: wallet_amount_gbp ?? 0,
      });
    } catch (error: any) {
      console.error("Payment preparation error:", error);
      showToast.error(
        error.response?.data?.message || "Failed to prepare payment",
      );
    } finally {
      setLoading(null);
    }
  };

  const handlePayWithCash = async () => {
    if (!booking) {
      return;
    }

    setLoading("cash");

    showAlert(
      "Pay with Cash",
      "Are you sure you want to pay with cash? You will need to pay the driver directly upon completion.",
      [
        {
          text: "Cancel",
          style: "cancel",
          onPress: () => setLoading(null),
        },
        {
          text: "Yes, Pay with Cash",
          onPress: async () => {
            try {
              await paymentService.payWithCash(booking.id);

              invalidateBookingQueries(qc, booking.id);

              const updatedBooking = await bookingService.getBooking(
                booking.id,
              );
              onSuccess(updatedBooking);

              showAlert(
                "Cash Payment Selected",
                "You have selected to pay with cash. Please ensure you have the cash ready to pay the driver upon completion.",
                [{ text: "OK" }],
              );
            } catch (error: any) {
              console.error("Cash payment error:", error);
              showToast.error(
                error.response?.data?.message ||
                  "Failed to select cash payment",
              );
            } finally {
              setLoading(null);
            }
          },
        },
      ],
    );
  };

  const isCustomer = isCustomerRole(user?.role);
  const isAccepted = booking.status === "accepted";
  const isNotPaid =
    !booking.payment ||
    (booking.payment.status !== "authorized" &&
      booking.payment.status !== "captured");
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const paymentIntentId = params.get("payment_intent");
    const clientSecret = params.get("payment_intent_client_secret");

    if (!paymentIntentId || !clientSecret || !user) {
      return;
    }

    if (!isCustomer || !isAccepted || !isNotPaid) {
      clearPaymentReturnParams();
      return;
    }

    if (handledPaymentIntentIds.has(paymentIntentId)) {
      return;
    }

    handledPaymentIntentIds.add(paymentIntentId);
    setLoading("pay");

    const completeRedirect = async () => {
      try {
        if (params.get("redirect_status") === "failed") {
          showToast.error("Payment was not completed. Please try again.");
          clearPaymentReturnParams();
          return;
        }

        const stripe = await getStripe();
        if (!stripe) {
          handledPaymentIntentIds.delete(paymentIntentId);
          showToast.error("Payment failed");
          return;
        }

        const { error, paymentIntent } =
          await stripe.retrievePaymentIntent(clientSecret);

        if (error) {
          showToast.error(error.message ?? "Payment failed");
          clearPaymentReturnParams();
          return;
        }

        if (!isPayableIntentStatus(paymentIntent?.status)) {
          showToast.error("Payment was not completed. Please try again.");
          clearPaymentReturnParams();
          return;
        }

        await recordAuthorizedPayment(qc, booking, paymentIntentId, onSuccess);
        void refreshUser?.();
        clearPaymentReturnParams();
      } catch (error: any) {
        const message = error.response?.data?.message as string | undefined;

        if (message === "Payment already exists for this booking") {
          clearPaymentReturnParams();
          invalidateBookingQueries(qc, booking.id);
          const updatedBooking = await bookingService.getBooking(booking.id);
          onSuccess(updatedBooking);
          return;
        }

        handledPaymentIntentIds.delete(paymentIntentId);
        console.error("Payment error:", error);
        showToast.error(message || "Payment failed");
      } finally {
        setLoading(null);
      }
    };

    void completeRedirect();
  }, [booking, isAccepted, isCustomer, isNotPaid, onSuccess, qc, refreshUser, user]);

  if (!isCustomer || !isAccepted || !isNotPaid) {
    return null;
  }

  return (
    <View style={styles.container}>
      <TouchableOpacity
        style={[styles.actionButton, styles.payButton]}
        onPress={() => setSummaryVisible(true)}
        disabled={loading !== null}
      >
        <Ionicons name="card" size={20} color="#fff" />
        <ThemedText style={styles.actionButtonText} type="defaultSemiBold">
          {loading === "pay"
            ? "Processing..."
            : `Pay Now (£${booking.pricing?.total_amount || booking.total_amount})`}
        </ThemedText>
      </TouchableOpacity>

      {booking?.customer?.accepts_cash && (
        <TouchableOpacity
          style={[styles.actionButton, styles.cashButton]}
          onPress={handlePayWithCash}
          disabled={loading !== null}
        >
          <Ionicons name="cash" size={20} color="#fff" />
          <ThemedText style={styles.actionButtonText} type="defaultSemiBold">
            {loading === "cash"
              ? "Processing..."
              : `Pay with Cash (£${booking.pricing?.total_amount || booking.total_amount})`}
          </ThemedText>
        </TouchableOpacity>
      )}

      <PaymentSummarySheet
        visible={summaryVisible}
        booking={booking}
        user={user}
        submitting={loading === "pay"}
        onClose={() => setSummaryVisible(false)}
        onConfirm={(useWalletBalance) => void handlePayNow(useWalletBalance)}
      />

      {paymentSession && (
        <WebPaymentModal
          booking={booking}
          clientSecret={paymentSession.clientSecret}
          paymentIntentId={paymentSession.paymentIntentId}
          amount={paymentSession.amount}
          walletAmount={paymentSession.walletAmount}
          onClose={() => setPaymentSession(null)}
          onSuccess={(updatedBooking) => {
            void refreshUser?.();
            onSuccess(updatedBooking);
          }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    gap: 12,
  },
  actionButtonText: {
    fontSize: 16,
    color: "#fff",
    fontWeight: "600",
  },
  payButton: {
    backgroundColor: BrandColors.success,
  },
  cashButton: {
    backgroundColor: "#FF9500",
  },
  disabledButton: {
    opacity: 0.7,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 480,
    backgroundColor: BrandColors.white,
    borderRadius: 16,
    overflow: "hidden",
  },
  modalContent: {
    padding: 24,
    gap: 16,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  modalTitle: {
    fontSize: 20,
    color: BrandColors.charcoal,
  },
  modalSubtitle: {
    color: BrandColors.darkGray,
    lineHeight: 20,
  },
  paymentElementContainer: {
    minHeight: 200,
  },
});
